import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../utils/helpers';
import SidePanel from './ui/SidePanel';

interface LogDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  log: any;
}

export default function LogDetailPanel({ isOpen, onClose, log }: LogDetailPanelProps) {
  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="日志详情"
      icon={<InformationCircleIcon className="h-5 w-5 text-sky-400" />}
    >
      {log ? <LogContent log={log} /> : (
        <div className="flex flex-col items-center justify-center h-full text-zinc-500">
          <InformationCircleIcon className="h-12 w-12 mb-4 opacity-20" />
          <p>请选择一条日志以查看详情</p>
        </div>
      )}
    </SidePanel>
  );
}

function LogContent({ log }: { log: any }) {
   const safeParseJSON = (jsonString: string | null): string => {
     if (!jsonString) return '{}';
     try {
       const parsed = JSON.parse(jsonString);
       return JSON.stringify(parsed, null, 2);
     } catch (error) {
       return jsonString;
     }
   };

   const highlightJSON = (json: string): JSX.Element => {
     if (!json || json === '{}') {
       return <span className="text-zinc-400">{json}</span>;
     }
     
     const lines = json.split('\n');
     return (
       <>
         {lines.map((line, lineIndex) => {
           const parts = line.split(/("[^"]+":|"[^"]+"|{|}|\[|\]|,|true|false|null|\d+\.?\d*)/g);
           
           return (
             <div key={lineIndex} className="flex">
               {parts.map((part, partIndex) => {
                 if (!part.trim()) return <span key={partIndex}>{part}</span>;
                 
                 if (part.match(/^"[^"]+":$/)) {
                   // Key with colon
                   return <span key={partIndex} className="text-blue-400">{part}</span>;
                 } else if (part.match(/^"[^"]+"$/)) {
                   // String value
                   return <span key={partIndex} className="text-zinc-300">{part}</span>;
                 } else if (part.match(/^(true|false|null)$/)) {
                   // Boolean/null
                   return <span key={partIndex} className="text-purple-400">{part}</span>;
                 } else if (part.match(/^\d+\.?\d*$/)) {
                   // Number
                   return <span key={partIndex} className="text-green-400">{part}</span>;
                 } else if (part.match(/^[\[\]{},]$/)) {
                   // Punctuation
                   return <span key={partIndex} className="text-zinc-500">{part}</span>;
                 }
                 
                 return <span key={partIndex}>{part}</span>;
               })}
             </div>
           );
         })}
       </>
     );
   };



  const getActionText = (action: string) => {
    const actionMap: Record<string, string> = {
      LOGIN: '登录',
      LOGOUT: '登出',
      RETRY_TASK: '重试任务',
      CANCEL_TASK: '取消任务',
      BATCH_CANCEL_TASKS: '批量取消任务',
      BATCH_DELETE_TASKS: '批量删除任务',
      VIEW_TASK: '查看任务',
      DELETE_TASK: '删除任务',
    };
    return actionMap[action] || action;
  };

  const getResourceTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      TASK: '任务',
      INSTANCE: '实例',
      ADMIN: '管理员',
    };
    return typeMap[type] || type;
  };

   return (
      <div className="space-y-8">
         <div>
           <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 mb-4">操作信息</h3>
           <dl className="space-y-4">
             <div className="flex flex-col gap-y-1">
               <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">操作人</dt>
               <dd className="text-sm font-medium text-zinc-200">{log.admin_id}</dd>
             </div>

             <div className="flex flex-col gap-y-1">
               <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">动作</dt>
               <dd>
                 <span className="inline-flex items-center rounded-md bg-blue-500/[8%] px-2 py-0.5 text-xs font-medium text-blue-400">
                   {getActionText(log.action)}
                 </span>
               </dd>
             </div>

             <div className="flex flex-col gap-y-1">
               <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">资源类型</dt>
               <dd className="text-sm font-medium text-zinc-200">
                 {getResourceTypeText(log.resource_type)}
               </dd>
             </div>

             <div className="flex flex-col gap-y-1">
               <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">资源 ID</dt>
               <dd className="text-sm font-medium text-zinc-200 font-mono">
                 {log.resource_id || '-'}
               </dd>
             </div>

             <div className="flex flex-col gap-y-1">
               <dt className="text-xs font-medium text-zinc-500 uppercase tracking-wider">发生时间</dt>
               <dd className="text-sm font-medium text-zinc-200 font-mono">
                 {formatDate(log.created_at)}
               </dd>
             </div>
           </dl>
         </div>

         {log.details && (
           <div>
             <div className="flex items-center justify-between mb-2">
               <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500">Payload</h3>
               <button 
                 onClick={() => navigator.clipboard.writeText(log.details)}
                 className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
               >
                 复制
               </button>
             </div>
             
             <div className="rounded-xl bg-black/50 p-4">
               <pre className="font-mono text-xs leading-relaxed text-zinc-400 overflow-x-hidden whitespace-pre-wrap break-all">
                 {highlightJSON(safeParseJSON(log.details))}
               </pre>
             </div>
           </div>
         )}
      </div>
    );
}